import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, Maximize2, Minimize2, ZoomIn, ZoomOut, RotateCw, Share2 } from 'lucide-react';

interface ImagePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  imageName: string;
  imageType?: string;
}

export const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  isOpen,
  onClose,
  imageUrl,
  imageName,
  imageType = 'image'
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Refs for throttling
  const wheelTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastWheelTime = useRef<number>(0);

  // Reset states when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setZoom(1);
      setRotation(0);
      setDragPosition({ x: 0, y: 0 });
      setIsLoading(true);
      setError(null);
    } else {
      // Cleanup timeouts when closing
      if (wheelTimeoutRef.current) {
        clearTimeout(wheelTimeoutRef.current);
        wheelTimeoutRef.current = null;
      }
    }
  }, [isOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wheelTimeoutRef.current) {
        clearTimeout(wheelTimeoutRef.current);
      }
    };
  }, []);

  const handleImageLoad = () => {
    setIsLoading(false);
    setError(null);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setError('Failed to load image');
  };

  const handleZoomIn = useCallback(() => {
    setZoom(prev => {
      const newZoom = Math.min(prev * 1.2, 5);
      return Math.round(newZoom * 100) / 100; // Round to 2 decimal places
    });
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => {
      const newZoom = Math.max(prev / 1.2, 0.1);
      return Math.round(newZoom * 100) / 100; // Round to 2 decimal places
    });
  }, []);

  const resetZoom = useCallback(() => {
    setZoom(1);
    setDragPosition({ x: 0, y: 0 });
  }, []);

  const handleRotate = useCallback(() => {
    setRotation(prev => (prev + 90) % 360);
  }, []);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  const handleDownload = useCallback(async () => {
    try {
      // Create a temporary link element to trigger download
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      
      // Extract file extension from URL or use default
      const urlParts = imageUrl.split('.');
      const extension = urlParts.length > 1 ? urlParts[urlParts.length - 1].split('?')[0] : 'jpg';
      const fileName = `${imageName.replace(/\.[^/.]+$/, '')}_full_resolution.${extension}`;
      
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback: open in new tab
      window.open(imageUrl, '_blank');
    }
  }, [imageUrl, imageName]);

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case '+':
        case '=':
          e.preventDefault();
          handleZoomIn();
          break;
        case '-':
          e.preventDefault();
          handleZoomOut();
          break;
        case '0':
          e.preventDefault();
          resetZoom();
          break;
        case 'r':
        case 'R':
          e.preventDefault();
          handleRotate();
          break;
        case 'd':
        case 'D':
          e.preventDefault();
          handleDownload();
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          toggleFullscreen();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, handleZoomIn, handleZoomOut, resetZoom, handleRotate, handleDownload, toggleFullscreen, onClose]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: imageName,
          url: imageUrl
        });
      } catch (error) {
        // Fallback to copying URL
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(imageUrl);
      // You could show a toast notification here
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  // Mouse drag handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoom > 1 && e.button === 0) { // Only left mouse button
      e.preventDefault();
      setIsDragging(true);
      setDragStart({
        x: e.clientX - dragPosition.x,
        y: e.clientY - dragPosition.y
      });
    }
  }, [zoom, dragPosition]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      e.preventDefault();
      setDragPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, zoom, dragStart]);

  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    if (e.button === 0) { // Only left mouse button
      setIsDragging(false);
    }
  }, []);

  // Throttled wheel zoom handler using DOM event listener to avoid passive event issues
  const wheelHandlerRef = useRef<HTMLDivElement | null>(null);
  
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    
    const now = Date.now();
    const timeSinceLastWheel = now - lastWheelTime.current;
    
    // Throttle to max 60fps (16ms between updates)
    if (timeSinceLastWheel < 16) {
      return;
    }
    
    lastWheelTime.current = now;
    
    // Clear any pending timeout
    if (wheelTimeoutRef.current) {
      clearTimeout(wheelTimeoutRef.current);
    }
    
    // Batch the zoom update
    wheelTimeoutRef.current = setTimeout(() => {
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      setZoom(prev => {
        const newZoom = Math.max(0.1, Math.min(5, prev * delta));
        return Math.round(newZoom * 100) / 100; // Round to 2 decimal places
      });
    }, 16);
  }, []);

  // Setup wheel event listener with passive: false
  useEffect(() => {
    const wheelHandler = wheelHandlerRef.current;
    if (!isOpen || !wheelHandler) return;

    wheelHandler.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      if (wheelHandler) {
        wheelHandler.removeEventListener('wheel', handleWheel);
      }
    };
  }, [isOpen, handleWheel]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={`fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50 ${
          isFullscreen ? 'p-0' : 'p-4'
        }`}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        {/* Header Controls */}
        <div className={`absolute top-0 left-0 right-0 bg-gradient-to-b from-black/50 to-transparent p-4 z-10 ${
          isFullscreen ? 'hidden' : 'block'
        }`}>
          <div className="flex items-center justify-between text-white">
            <div className="flex items-center space-x-4">
              <h3 className="text-lg font-medium truncate max-w-md">{imageName}</h3>
              <span className="text-sm text-gray-300 capitalize">{imageType}</span>
            </div>
            
            {/* Control buttons */}
            <div className="flex items-center space-x-2">
              <button
                onClick={handleZoomOut}
                disabled={zoom <= 0.1}
                className="p-2 bg-black/30 hover:bg-black/50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Zoom Out (-)"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              
              <button
                onClick={resetZoom}
                className="px-3 py-2 bg-black/30 hover:bg-black/50 rounded-lg text-sm font-medium transition-colors"
                title="Reset Zoom (0)"
              >
                {Math.round(zoom * 100)}%
              </button>
              
              <button
                onClick={handleZoomIn}
                disabled={zoom >= 5}
                className="p-2 bg-black/30 hover:bg-black/50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Zoom In (+)"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
              
              <button
                onClick={handleRotate}
                className="p-2 bg-black/30 hover:bg-black/50 rounded-lg transition-colors"
                title="Rotate (R)"
              >
                <RotateCw className="w-4 h-4" />
              </button>
              
              <button
                onClick={toggleFullscreen}
                className="p-2 bg-black/30 hover:bg-black/50 rounded-lg transition-colors"
                title="Toggle Fullscreen (F)"
              >
                {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              
              <button
                onClick={handleShare}
                className="p-2 bg-black/30 hover:bg-black/50 rounded-lg transition-colors"
                title="Share"
              >
                <Share2 className="w-4 h-4" />
              </button>
              
              <button
                onClick={onClose}
                className="p-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                title="Close (Esc)"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Image Container */}
        <motion.div
          ref={wheelHandlerRef}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          className={`relative flex items-center justify-center ${
            isFullscreen ? 'w-full h-full' : 'w-full h-full max-w-7xl max-h-full'
          }`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{ cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
        >
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex flex-col items-center space-y-4">
                <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span className="text-white text-sm">Loading image...</span>
              </div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex flex-col items-center space-y-4 text-white">
                <X className="w-16 h-16 text-red-500" />
                <p className="text-lg">{error}</p>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {!error && (
            <img
              src={imageUrl}
              alt={imageName}
              onLoad={handleImageLoad}
              onError={handleImageError}
              className={`max-w-full max-h-full object-contain transition-transform duration-200 select-none ${
                isLoading ? 'opacity-0' : 'opacity-100'
              }`}
              style={{
                transform: `scale(${zoom}) rotate(${rotation}deg) translate(${dragPosition.x}px, ${dragPosition.y}px)`,
                transformOrigin: 'center center'
              }}
              draggable={false}
            />
          )}
        </motion.div>

        {/* Footer with Download Button and Info */}
        {!isFullscreen && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
            <div className="flex items-center justify-between">
              {/* Tips */}
              <div className="flex items-center text-white text-sm space-x-4">
                <span>Mouse wheel: zoom</span>
                <span>•</span>
                <span>Drag: pan</span>
                <span>•</span>
                <span>Esc: close</span>
              </div>
              
              {/* Download Button */}
              <button
                onClick={handleDownload}
                className="flex items-center space-x-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium shadow-lg"
                title="Download Full Resolution (D)"
              >
                <Download className="w-5 h-5" />
                <span>Download Full Resolution</span>
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default ImagePreviewModal;